import React, { useState, useEffect } from 'react';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    subcategories: [],
    productExtraImagesEnabled: false,
    productExtraImages: []
  });
  const [subcategoryInput, setSubcategoryInput] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [togglingId, setTogglingId] = useState(null);
  const [nameError, setNameError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/categories?includeInactive=true`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Error al cargar categorías');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isDuplicate = categories.some(
      cat => cat.name.toLowerCase() === formData.name.toLowerCase() && cat._id !== editingId
    );
    
    if (isDuplicate) {
      setNameError('Ya existe una categoría con este nombre');
      return;
    }
    
    setLoading(true);
    setError('');
    setNameError('');

    try {
      const url = editingId 
        ? `${process.env.REACT_APP_API_URL}/categories/${editingId}`
        : `${process.env.REACT_APP_API_URL}/categories`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Error al guardar categoría');
      }

      await fetchCategories();
      resetForm();
      setError('');
    } catch (error) {
      console.error('Error saving category:', error);
      setError('Error al guardar categoría');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      subcategories: category.subcategories || [],
      productExtraImagesEnabled: Boolean(category.productExtraImagesEnabled),
      productExtraImages: category.productExtraImages || []
    });
    setEditingId(category._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta categoría?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/categories/${id}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          throw new Error('Error al eliminar categoría');
        }

        await fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        setError('Error al eliminar categoría');
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setTogglingId(id);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/categories/${id}/toggle-status`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error('Error al cambiar estado de categoría');
      }

      await fetchCategories();
    } catch (error) {
      console.error('Error toggling category status:', error);
      setError('Error al cambiar estado de categoría');
    } finally {
      setTogglingId(null);
    }
  };

  const addSubcategory = () => {
    if (subcategoryInput.name.trim()) {
      setFormData(prev => ({
        ...prev,
        subcategories: [...prev.subcategories, subcategoryInput]
      }));
      setSubcategoryInput({ name: '', description: '' });
    }
  };

  const removeSubcategory = (index) => {
    setFormData(prev => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      image: '',
      subcategories: [],
      productExtraImagesEnabled: false,
      productExtraImages: []
    });
    setEditingId(null);
    setSubcategoryInput({ name: '', description: '' });
    setShowForm(false);
    setNameError('');
  };

  // Filter
  const filteredCategories = categories.filter(cat => {
    const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' ? cat.isActive : !cat.isActive);
    const matchesSearch = !searchQuery || cat.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const activeCount = categories.filter(c => c.isActive).length;
  const inactiveCount = categories.filter(c => !c.isActive).length;
  const totalSubcategories = categories.reduce((sum, c) => sum + (c.subcategories?.length || 0), 0);

  return (
    <div className="catmgr-panel">
      {/* Header */}
      <div className="catmgr-header">
        <div className="catmgr-header-text">
          <h1>Gestión de Categorías</h1>
          <p>Organiza tu catálogo con categorías y subcategorías</p>
        </div>
        <button className="catmgr-add-btn" onClick={() => { resetForm(); setShowForm(true); }}>
          <i className="fas fa-plus"></i>
          Nueva Categoría
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="catmgr-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={() => setError('')}><i className="fas fa-times"></i></button>
        </div>
      )}

      {/* KPI Row */}
      <div className="catmgr-kpi-row">
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
            <i className="fas fa-tags"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{categories.length}</span>
            <span className="catmgr-kpi-label">Total Categorías</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{activeCount}</span>
            <span className="catmgr-kpi-label">Activas</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
            <i className="fas fa-eye-slash"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{inactiveCount}</span>
            <span className="catmgr-kpi-label">Inactivas</span>
          </div>
        </div>
        <div className="catmgr-kpi">
          <div className="catmgr-kpi-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
            <i className="fas fa-sitemap"></i>
          </div>
          <div className="catmgr-kpi-info">
            <span className="catmgr-kpi-value">{totalSubcategories}</span>
            <span className="catmgr-kpi-label">Subcategorías</span>
          </div>
        </div>
      </div>

      {/* Form (collapsible) */}
      {showForm && (
        <div className="catmgr-form-card">
          <div className="catmgr-form-header">
            <h3>{editingId ? 'Editar Categoría' : 'Nueva Categoría'}</h3>
            <button className="catmgr-form-close" onClick={resetForm}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="catmgr-form-grid">
              <div className="catmgr-form-field">
                <label>Nombre *</label>
                <input
                  type="text"
                  className={nameError ? 'has-error' : ''}
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    setNameError('');
                  }}
                  placeholder="Ej: Remeras, Buzos, Accesorios..."
                  required
                />
                {nameError && <span className="catmgr-field-error">{nameError}</span>}
              </div>
              <div className="catmgr-form-field">
                <label>URL de Imagen</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
            </div>

            <div className="catmgr-form-field">
              <label>Descripción</label>
              <textarea
                rows="2"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción opcional de la categoría..."
              />
            </div>

            {/* Subcategories */}
            <div className="catmgr-subcat-section">
              <label>Subcategorías</label>
              <div className="catmgr-subcat-input-row">
                <input
                  type="text"
                  placeholder="Nombre de subcategoría"
                  value={subcategoryInput.name}
                  onChange={(e) => setSubcategoryInput(prev => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcategory(); }}}
                />
                <input
                  type="text"
                  placeholder="Descripción (opcional)"
                  value={subcategoryInput.description}
                  onChange={(e) => setSubcategoryInput(prev => ({ ...prev, description: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcategory(); }}}
                />
                <button type="button" className="catmgr-subcat-add-btn" onClick={addSubcategory}>
                  <i className="fas fa-plus"></i>
                </button>
              </div>
              {formData.subcategories.length > 0 && (
                <div className="catmgr-subcat-tags">
                  {formData.subcategories.map((sub, index) => (
                    <span key={index} className="catmgr-subcat-tag">
                      {sub.name}
                      {sub.description && <small> — {sub.description}</small>}
                      <button type="button" onClick={() => removeSubcategory(index)}>
                        <i className="fas fa-times"></i>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="catmgr-form-actions">
              <button type="submit" className="catmgr-submit-btn" disabled={loading}>
                {loading ? 'Guardando...' : (editingId ? 'Actualizar Categoría' : 'Crear Categoría')}
              </button>
              <button type="button" className="catmgr-cancel-btn" onClick={resetForm}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Toolbar */}
      <div className="catmgr-toolbar">
        <div className="catmgr-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Buscar categorías..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="catmgr-filter-tabs">
          <button
            className={`catmgr-filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Todas <span className="catmgr-filter-count">{categories.length}</span>
          </button>
          <button
            className={`catmgr-filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
            onClick={() => setFilterStatus('active')}
          >
            Activas <span className="catmgr-filter-count">{activeCount}</span>
          </button>
          <button
            className={`catmgr-filter-btn ${filterStatus === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilterStatus('inactive')}
          >
            Inactivas <span className="catmgr-filter-count">{inactiveCount}</span>
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      {filteredCategories.length === 0 ? (
        <div className="catmgr-empty">
          <i className="fas fa-folder-open"></i>
          <h3>No hay categorías</h3>
          <p>{searchQuery ? 'No se encontraron resultados' : 'Crea tu primera categoría para organizar los productos'}</p>
        </div>
      ) : (
        <div className="catmgr-grid">
          {filteredCategories.map(category => (
            <div key={category._id} className={`catmgr-card ${!category.isActive ? 'inactive' : ''}`}>
              <div className="catmgr-card-header">
                <div className="catmgr-card-title">
                  <h3>{category.name}</h3>
                  <span className={`catmgr-status-badge ${category.isActive ? 'active' : 'inactive'}`}>
                    {category.isActive ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <label className="catmgr-toggle">
                  <input
                    type="checkbox"
                    checked={category.isActive}
                    onChange={() => handleToggleStatus(category._id, category.isActive)}
                    disabled={togglingId === category._id}
                  />
                  <span className="catmgr-toggle-slider"></span>
                </label>
              </div>

              {category.description && (
                <p className="catmgr-card-desc">{category.description}</p>
              )}

              {category.subcategories?.length > 0 ? (
                <div className="catmgr-card-subcats">
                  {category.subcategories.map((sub, index) => (
                    <span key={index} className="catmgr-subcat-pill">{sub.name}</span>
                  ))}
                </div>
              ) : (
                <p className="catmgr-no-subcats">Sin subcategorías</p>
              )}

              <div className="catmgr-card-actions">
                <button className="catmgr-action-edit" onClick={() => handleEdit(category)}>
                  <i className="fas fa-edit"></i> Editar
                </button>
                <button className="catmgr-action-delete" onClick={() => handleDelete(category._id)}>
                  <i className="fas fa-trash"></i> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
